/**
 * GA4 ecommerce - interaction half.
 *
 * snippets/ga4-ecommerce.liquid loads gtag, fires the page-level events
 * (view_item, view_item_list, view_cart, search) and parks the item payloads on
 * `window.__tzGA4`. This file adds the events that depend on the visitor doing
 * something:
 *
 *   select_item      clicking a product card in a grid
 *   add_to_cart      submitting the PDP form, or any /cart/add form
 *   view_cart        opening the cart drawer (the cart PAGE is handled in Liquid)
 *   begin_checkout   pressing the checkout button
 *
 * Design rules this file sticks to:
 *  - Never call preventDefault, never wrap or replace a submit handler. The
 *    checkout and add-to-cart paths are native form submits that Razorpay Magic
 *    Checkout depends on; we only listen, in the capture phase, and let the
 *    event continue untouched.
 *  - Delegate from document so it keeps working for cards and drawers that are
 *    injected after load.
 *  - Fail silently. Analytics must never be able to break a purchase.
 */
(function () {
  'use strict';

  var G = (window.__tzGA4 = window.__tzGA4 || {});
  function send(name, params) {
    try {
      if (!once(name)) return;
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
    } catch (e) { /* analytics must never throw into the page */ }
  }
  function currency() { return G.currency || 'INR'; }

  /* Same event twice inside 2.5s is a double-bind or a server+client overlap,
     not two real actions. GA4 would count both, so drop the second. */
  var recent = {};
  function once(name) {
    var now = Date.now();
    if (recent[name] && now - recent[name] < 2500) return false;
    recent[name] = now;
    return true;
  }

  /* ---------- select_item: a product card was clicked ---------------------
     Cards render as <a class="v-card" href="/products/handle?variant=...">.
     Match the clicked handle against the list payload the page already has, so
     we report the same item_id and index GA4 saw in view_item_list.          */
  document.addEventListener('click', function (ev) {
    var card = ev.target && ev.target.closest && ev.target.closest('a[href*="/products/"]');
    if (!card) return;
    var handle = (card.getAttribute('href') || '').split('?')[0].split('/products/')[1];
    if (!handle) return;

    var item = null;
    if (G.list && G.list.items) {
      // Cards do not carry the sku code, so fall back to matching on title text.
      var label = (card.getAttribute('aria-label') || card.textContent || '').trim().toLowerCase();
      for (var i = 0; i < G.list.items.length; i++) {
        var name = (G.list.items[i].item_name || '').toLowerCase();
        if (name && label.indexOf(name) !== -1) { item = G.list.items[i]; break; }
      }
    }
    if (!item) item = { item_id: handle, item_name: handle, quantity: 1 };

    send('select_item', {
      item_list_name: (G.list && G.list.name) || 'Grid',
      items: [item]
    });
  }, true);

  /* ---------- add_to_cart: the PDP form was submitted ---------------------
     Capture-phase listener on submit. We read the chosen quantity and variant
     from the form itself so a size change is reflected.                      */
  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    // Decide after the page's own handlers ran: if one of them took over the
    // submit (preventDefault + fetch), the fetch observer below reports the add
    // from Shopify's reply instead. Only a real native submit is counted here.
    setTimeout(function () { if (!ev.defaultPrevented) nativeAdd(form); }, 0);
  }, true);

  function nativeAdd(form) {

    var qtyEl = form.querySelector('[name="quantity"]');
    var qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
    var base = G.item || {};
    var item = {
      item_id: base.item_id,
      item_name: base.item_name,
      item_brand: base.item_brand,
      item_category: base.item_category,
      item_variant: base.item_variant,
      price: base.price,
      quantity: qty
    };

    // Reflect the size the visitor actually picked, if the form exposes it.
    var variantEl = form.querySelector('[name="id"]');
    if (variantEl) {
      var opt = variantEl.options ? variantEl.options[variantEl.selectedIndex] : null;
      if (opt && opt.textContent) item.item_variant = opt.textContent.trim();
    }

    send('add_to_cart', {
      currency: currency(),
      value: (item.price || 0) * qty,
      items: [item]
    });
  }

  /* ---------- add_to_cart: an Ajax add succeeded ---------------------------
     The PDP button, card quick-add and the look rail all add with
     fetch('/cart/add.js'), never a native submit, so until 2026-11-04 GA4 saw
     almost no bag adds while Shopify and Meta counted them. This observes
     window.fetch passively: the page gets back the very same promise and
     response it would have got without us; we only read a CLONE of a
     successful /cart/add reply, which is the line item(s) Shopify just added. */
  if (typeof window.fetch === 'function' && !window.fetch.__tzGA4) {
    var origFetch = window.fetch;
    var observed = function (input, init) {
      var p = origFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
          p.then(function (res) {
            if (!res || !res.ok) return;
            res.clone().json().then(reportAjaxAdd).catch(function () {});
          }).catch(function () {});
        }
      } catch (e) { /* never break the page's own request */ }
      return p;
    };
    observed.__tzGA4 = true;
    window.fetch = observed;
  }

  function reportAjaxAdd(reply) {
    var lines = reply && reply.items ? reply.items : (reply && reply.variant_id ? [reply] : []);
    if (!lines.length) return;
    var value = 0;
    var items = lines.map(function (li) {
      var price = (li.final_price || li.price || 0) / 100;
      var qty = li.quantity || 1;
      value += price * qty;
      // Same item_id as view_item / view_item_list (the Tenzen code, not the
      // Qikink/Unitee variant SKU): on the PDP it is parked on G.item.
      // Off the PDP (card quick-add, look rail) the code comes from the list
      // payload the page already sent with view_item_list.
      var sameProduct = G.item && G.item.item_name && G.item.item_name === li.product_title;
      var listed = null;
      if (!sameProduct && G.list && G.list.items) {
        for (var i = 0; i < G.list.items.length; i++) {
          if (G.list.items[i].item_name === li.product_title) { listed = G.list.items[i]; break; }
        }
      }
      return {
        item_id: (sameProduct && G.item.item_id) || (listed && listed.item_id) || li.sku || String(li.variant_id),
        item_name: li.product_title,
        item_brand: li.vendor,
        item_category: li.product_type,
        item_variant: li.variant_title,
        price: price,
        quantity: qty
      };
    });
    send('add_to_cart', { currency: currency(), value: value, items: items });
  }

  /* ---------- view_cart: the drawer was opened ---------------------------
     Cart PAGE view_cart is emitted server-side. This covers the drawer, which
     is how most visitors actually see their cart. Read live contents from
     /cart.js so the payload matches what is really in there.                 */
  var lastDrawer = 0;
  document.addEventListener('click', function (ev) {
    var t = ev.target && ev.target.closest && ev.target.closest('[data-open-cart]');
    if (!t) return;
    var now = Date.now();
    if (now - lastDrawer < 1500) return;   // ignore double fires from nested handlers
    lastDrawer = now;

    // The PDP add button doubles as the drawer opener, so at click time the
    // line item may not exist yet. Wait for the add to complete before reading.
    var delay = t.getAttribute('name') === 'add' ? 1200 : 150;
    setTimeout(readCartAndSend, delay);
  }, true);

  function readCartAndSend() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;
        send('view_cart', {
          currency: cart.currency || currency(),
          value: (cart.total_price || 0) / 100,
          items: cart.items.map(function (li) {
            return {
              item_id: li.sku || li.variant_id,
              item_name: li.product_title,
              item_variant: li.variant_title,
              price: (li.final_price || 0) / 100,
              quantity: li.quantity
            };
          })
        });
      })
      .catch(function () { /* ignore */ });
  }

  /* ---------- begin_checkout: the checkout button was pressed -------------
     `name="checkout"` is the native submit Magic Checkout relies on, so this
     listens only. Fires on both the cart page and the drawer.                */
  document.addEventListener('click', function (ev) {
    var el = ev.target && ev.target.closest && ev.target.closest('[name="checkout"], [href="/checkout"]');
    if (!el) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        send('begin_checkout', {
          currency: cart.currency || currency(),
          value: (cart.total_price || 0) / 100,
          items: cart.items.map(function (li) {
            return {
              item_id: li.sku || li.variant_id,
              item_name: li.product_title,
              item_variant: li.variant_title,
              price: (li.final_price || 0) / 100,
              quantity: li.quantity
            };
          })
        });
      })
      .catch(function () { /* ignore */ });
  }, true);
})();
